import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="frame-transition" on a <turbo-frame>.
// Animates content swaps inside the frame (fade out, smooth height change,
// fade in) for both frame navigations (Edit / Cancel links) and
// turbo_stream replacements of the frame (Save).
export default class extends Controller {
  static fadeOutMs = 120
  static fadeInMs = 250

  connect() {
    this.onBeforeFrameRender = this.beforeFrameRender.bind(this)
    this.onBeforeStreamRender = this.beforeStreamRender.bind(this)
    this.element.addEventListener("turbo:before-frame-render", this.onBeforeFrameRender)
    document.addEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-frame-render", this.onBeforeFrameRender)
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
  }

  get reduceMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Edit / Cancel: Turbo re-renders the frame's children in place
  async beforeFrameRender(event) {
    if (this.reduceMotion) return

    event.preventDefault()
    const frame = this.element
    const startHeight = frame.offsetHeight

    await frame.animate({ opacity: [1, 0] }, { duration: this.constructor.fadeOutMs, easing: "ease-in" })
      .finished.catch(() => {})

    // Pin the old size so the new content doesn't flash at full height
    frame.style.opacity = "0"
    frame.style.height = `${startHeight}px`
    frame.style.overflow = "hidden"

    const rendered = new Promise(resolve => frame.addEventListener("turbo:frame-render", resolve, { once: true }))
    event.detail.resume()
    await rendered

    this.growIn(frame, startHeight)
  }

  // Save: the server replies with turbo_stream.replace on this frame,
  // which swaps the whole element (no frame render events fire)
  beforeStreamRender(event) {
    const stream = event.target
    if (stream.getAttribute("action") !== "replace") return
    if (stream.getAttribute("target") !== this.element.id) return
    if (this.reduceMotion) return

    const id = this.element.id
    const fadeOutMs = this.constructor.fadeOutMs
    const defaultRender = event.detail.render

    event.detail.render = async (streamElement) => {
      const oldElement = document.getElementById(id)
      const startHeight = oldElement ? oldElement.offsetHeight : null

      if (oldElement) {
        await oldElement.animate({ opacity: [1, 0] }, { duration: fadeOutMs, easing: "ease-in" })
          .finished.catch(() => {})
        oldElement.style.opacity = "0"
      }

      await defaultRender(streamElement)

      const newElement = document.getElementById(id)
      if (newElement && startHeight !== null) {
        newElement.style.opacity = "0"
        this.growIn(newElement, startHeight)
      }
    }
  }

  // Animate from the pinned old height to the element's natural height
  async growIn(element, startHeight) {
    element.style.height = ""
    const targetHeight = element.offsetHeight
    element.style.overflow = "hidden"

    await element.animate(
      { height: [`${startHeight}px`, `${targetHeight}px`], opacity: [0, 1] },
      { duration: this.constructor.fadeInMs, easing: "ease" }
    ).finished.catch(() => {})

    element.style.opacity = ""
    element.style.overflow = ""
    element.style.height = ""
  }
}
